import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DEFAULT_PRESET,
  DateRange,
  PERIOD_PRESETS,
  PeriodPreset,
  isYmd,
  presetRange,
} from "./analyticsUtils";

const STORAGE_KEY = "analytics.period";

type Stored = { preset: PeriodPreset; from?: string; to?: string };

function readStored(): Stored | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Stored>;
    if (!parsed || !PERIOD_PRESETS.some((p) => p.key === parsed.preset)) return null;
    return {
      preset: parsed.preset as PeriodPreset,
      from: isYmd(parsed.from) ? parsed.from : undefined,
      to: isYmd(parsed.to) ? parsed.to : undefined,
    };
  } catch {
    return null;
  }
}

function writeStored(value: Stored) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* storage unavailable (private window, quota) - the period simply does not persist */
  }
}

export interface AnalyticsPeriod extends DateRange {
  preset: PeriodPreset;
  setPreset: (preset: PeriodPreset) => void;
  setCustom: (range: Partial<DateRange>) => void;
}

/**
 * The selected period, shared by every Analytics tab through sessionStorage so
 * switching tabs keeps it. Presets are re-resolved on each mount (a stored
 * "Last 7 days" from yesterday must mean today's last 7 days); only a custom
 * range stores its dates.
 */
export function useAnalyticsPeriod(): AnalyticsPeriod {
  const [state, setState] = useState<Stored>(() => {
    const stored = readStored();
    if (!stored) return { preset: DEFAULT_PRESET };
    return stored.preset === "custom" ? stored : { preset: stored.preset };
  });

  useEffect(() => {
    writeStored(state);
  }, [state]);

  const range = useMemo(
    () => presetRange(state.preset, undefined, { from: state.from, to: state.to }),
    [state.preset, state.from, state.to]
  );

  const setPreset = useCallback((preset: PeriodPreset) => {
    setState((prev) => {
      if (preset === "custom") {
        // Start the custom range from whatever was showing so the inputs are not blank.
        const current = presetRange(prev.preset, undefined, { from: prev.from, to: prev.to });
        return { preset, from: current.from, to: current.to };
      }
      return { preset };
    });
  }, []);

  const setCustom = useCallback((next: Partial<DateRange>) => {
    setState((prev) => ({
      preset: "custom",
      from: isYmd(next.from) ? next.from : prev.from,
      to: isYmd(next.to) ? next.to : prev.to,
    }));
  }, []);

  return { preset: state.preset, from: range.from, to: range.to, setPreset, setCustom };
}
